/**
 * Épingles d'affluence (touche I) : au-dessus de chaque remontée, comptoir,
 * restaurant et de l'accueil, une étiquette donne son état en un coup d'œil
 * (attente, parc de skis, places, visiteurs), colorée par niveau de
 * congestion. Le contenu suit le HUD (~6 fois/s) ; la position suit la
 * caméra à chaque image, par un rappel du moteur, sans re-rendu Preact.
 */
import { useEffect, useRef } from 'preact/hooks';
import { hud, focusEntity } from './context';
import { FACILITY_ICON, levelLabel } from './labels';
import { liftIcon } from './build-cats';
import { liveFacilities, type LiveFacility } from './live';
import { ui } from './ui-state';

export function Pins() {
  void ui.tick.value;
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const engine = hud.engine;
    if (!engine) return;
    const p = { x: 0, y: 0, dist: 0, front: false };
    const hook = () => {
      const el = root.current;
      if (!el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      for (const child of Array.from(el.children) as HTMLElement[]) {
        engine.project(Number(child.dataset.x), Number(child.dataset.h), Number(child.dataset.z), p);
        if (!p.front || p.dist > 3400 || p.x < -80 || p.x > w + 80 || p.y < -20 || p.y > h + 60) {
          child.style.visibility = 'hidden';
          continue;
        }
        const scale = Math.max(0.6, Math.min(1, 650 / Math.max(1, p.dist)));
        child.style.visibility = 'visible';
        child.style.transform = `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) translate(-50%, -100%) scale(${scale.toFixed(3)})`;
        child.style.zIndex = String(Math.max(1, Math.round(5000 - p.dist)));
      }
    };
    engine.frameHooks.add(hook);
    return () => {
      engine.frameHooks.delete(hook);
    };
  }, []);
  const store = hud.store;
  const r = store?.replica;
  const show = !!store && !!r && ui.pins.value && !ui.build.value && !ui.buildMenu.value;
  // Une épingle par bâtiment : le chalet (caisses + restaurant) montre son service le plus tendu.
  const best = new Map<string, LiveFacility>();
  if (show) {
    for (const f of liveFacilities(r!, store!.map.features.village)) {
      if (f.kind === 'piste') continue;
      const id = f.kind === 'accueil' ? 'accueil' : f.ref ? `${f.ref.kind}${f.ref.id}` : f.key;
      const cur = best.get(id);
      if (!cur || f.level > cur.level) best.set(id, f);
    }
  }
  const list = [...best.values()];
  return (
    <div class="g-pins" ref={root} aria-hidden={!show}>
      {list.map((f) => {
        const Icon = f.kind === 'lift' && f.ref ? liftIcon(r!.lifts.get(f.ref.id)?.kind ?? 'telesiege') : FACILITY_ICON[f.kind];
        return (
          <button
            type="button"
            key={f.key}
            class={`g-pin g-pin-l${Math.max(-1, Math.min(3, f.level))}`}
            data-x={f.pos[0]}
            data-z={f.pos[1]}
            data-h={f.height}
            style={{ visibility: 'hidden' }}
            title={`${f.label} : ${levelLabel(f.level)}${f.quota ? `, ${f.quota.label.toLowerCase()} ${Math.round(f.quota.used)} sur ${Math.round(f.quota.cap)}` : ''}${f.lost ? `, ${f.lost} perdus aujourd'hui` : ''}`}
            onClick={() => {
              if (f.ref) focusEntity(f.ref.kind, f.ref.id);
              if (f.ref?.kind === 'building') ui.inspectorService.value = f.key;
            }}
          >
            <span class="g-pin-ic">
              <Icon size={13} />
            </span>
            <span class="g-pin-val num">{f.short}</span>
          </button>
        );
      })}
    </div>
  );
}
